import { useState, useEffect, useMemo } from "react"; // Import useMemo
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCustomerByIdCard, getCustomers } from "@/integrations/supabase/services"; // Import getCustomers
import { Customer } from "@/types/models";
import AddCustomerModal from "./AddCustomerModal";

interface CustomerSelectorProps {
  selectedCustomer: Customer | null;
  onCustomerSelect: (customer: Customer | null) => void;
  searchTerm: string;
  onSearchChange: (search: string) => void;
}

const CustomerSelector = ({ 
  selectedCustomer, 
  onCustomerSelect, 
  onSearchChange 
}: CustomerSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]); // State for all customers
  const [isLoadingAllCustomers, setIsLoadingAllCustomers] = useState(false); // State for loading all customers

  // Fetch limited customers on mount
  useEffect(() => {
    const fetchInitialCustomers = async () => {
      setIsLoadingAllCustomers(true);
      try {
        const customers = await getCustomers(20); // Limita a 20 clientes
        setAllCustomers(customers);
      } catch (error) {
        console.error("Error fetching all customers:", error);
        setAllCustomers([]);
      } finally {
        setIsLoadingAllCustomers(false);
      }
    };
    fetchInitialCustomers();
  }, []);

  // Server-side search by ID card
  useEffect(() => {
    const searchCustomers = async () => {
      if (internalSearchTerm.length >= 4) {
        setIsSearching(true);
        try {
          const customer = await getCustomerByIdCard(internalSearchTerm);
          setSearchResults(customer ? [customer] : []);
        } catch (error) {
          console.error('Error searching customers:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(searchCustomers, 300);
    return () => clearTimeout(timeoutId);
  }, [internalSearchTerm]);

  const displayedCustomers = useMemo(() => {
    if (internalSearchTerm.length === 0) {
      return allCustomers;
    }
    if (internalSearchTerm.length > 0 && internalSearchTerm.length < 4) {
      return [];
    }
    return searchResults;
  }, [allCustomers, internalSearchTerm, searchResults]);

  const handleSuccess = (newCustomer: Customer) => {
    setAllCustomers(prev => [...prev, newCustomer].sort((a, b) => a.name.localeCompare(b.name))); // Add to allCustomers and sort
    onCustomerSelect(newCustomer);
    onSearchChange(newCustomer.id_card.toString());
    setInternalSearchTerm(newCustomer.id_card.toString());
    setIsModalOpen(false);
    setOpen(false);
  };

  const handleSearchValueChange = (value: string) => {
    setInternalSearchTerm(value);
    onSearchChange(value);
  };

  const handleCustomerSelection = (customer: Customer) => {
    onCustomerSelect(customer);
    onSearchChange(customer.id_card.toString());
    setInternalSearchTerm(customer.id_card.toString());
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedCustomer 
              ? `${selectedCustomer.name} (${selectedCustomer.id_card})`
              : "Seleccione un cliente o busque por cédula"
            }
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent style={{ width: 'var(--radix-popover-trigger-width)' }} className="p-0">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder="Buscar por cédula..." 
              value={internalSearchTerm}
              onValueChange={handleSearchValueChange}
            />
            <CommandList>
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    setIsModalOpen(true);
                    setOpen(false);
                  }}
                  className="text-primary bg-primary/10"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar Nuevo Cliente...
                </CommandItem>

                {isLoadingAllCustomers ? (
                  <CommandItem disabled>Cargando clientes...</CommandItem>
                ) : isSearching ? (
                  <CommandItem disabled>Buscando cliente...</CommandItem>
                ) : internalSearchTerm.length > 0 && internalSearchTerm.length < 4 ? (
                  <CommandItem disabled>Ingrese al menos 4 dígitos para buscar por cédula.</CommandItem>
                ) : displayedCustomers.length > 0 ? (
                  displayedCustomers.map((customer) => (
                      <CommandItem
                        key={customer.id}
                        onSelect={() => handleCustomerSelection(customer)}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedCustomer?.id === customer.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col">
                          <span>{customer.name} <b>( {customer.id_card} )</b></span>
                          {/* <span className="text-sm text-gray-500">Cédula: {customer.id_card}</span> */}
                          {/* <span className="text-sm text-gray-500">Teléfono: {customer.phone}</span> */}
                        </div>
                      </CommandItem>
                    ))
                ) : internalSearchTerm.length >= 4 ? (
                  <CommandEmpty>No se encontró ningún cliente con esa cédula.</CommandEmpty>
                ) : (
                  // This case handles internalSearchTerm.length === 0 && displayedCustomers.length === 0 (i.e., allCustomers is empty)
                  <CommandEmpty>No hay clientes registrados. Agregue uno nuevo.</CommandEmpty>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <AddCustomerModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSuccess={handleSuccess}
      />
    </>
  );
};

export default CustomerSelector;
